/**
 * useDossier — Frontend hook for consuming dossier data.
 *
 * Supports two modes:
 * 1. Mock adapter (P3): Composes existing addressService calls, simulates SSE
 * 2. Real SSE (P6): Connects to /dossier/stream endpoint
 *
 * Mode selected by VITE_USE_DOSSIER_SSE env var.
 *
 * Key design:
 * - Received sections cached in useRef (not state) — reconnect never clears populated data
 * - Exponential backoff reconnect (1s, 2s, 3s · max 3 attempts)
 * - EventSource.close() on 'done' event, not just unmount (SSE connection limit fix)
 * - isMounted guards prevent state updates after unmount or address change
 * - Stale-response guards in all async paths
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { addressService } from '../services/api';

const USE_SSE = import.meta.env.VITE_USE_DOSSIER_SSE === 'true';
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';
const MAX_RECONNECTS = 3;
/** Per-section fetch timeout (ms). Prevents spinner hang when backend is slow/unreachable. */
const SECTION_TIMEOUT_MS = 20_000;

const DOSSIER_KEYS = ['identity', 'ofac', 'profile', 'bridges'] as const;
type DossierKey = typeof DOSSIER_KEYS[number];

export interface DossierSection {
    status?: string;
    reason?: string;
    fetched_at?: string;
    [key: string]: any;
}

export type DossierPayload = Partial<Record<DossierKey, DossierSection>>;

export interface UseDossierReturn {
    sections: DossierPayload;
    loadingKeys: Set<DossierKey>;
    reconnectCount: number;
    isComplete: boolean;
    hasError: boolean;
    refresh: (key?: DossierKey) => void;
}

export function useDossier(chain: string | null, address: string | null): UseDossierReturn {
    const receivedSections = useRef<DossierPayload>({});
    const [sections, setSections] = useState<DossierPayload>({});
    const [loadingKeys, setLoadingKeys] = useState<Set<DossierKey>>(new Set(DOSSIER_KEYS));
    const [reconnectCount, setReconnectCount] = useState(0);
    const [isComplete, setIsComplete] = useState(false);
    const esRef = useRef<EventSource | null>(null);

    const markSection = useCallback((key: DossierKey, data: DossierSection) => {
        receivedSections.current[key] = data;
        setSections({ ...receivedSections.current });
        setLoadingKeys(prev => {
            const next = new Set(prev);
            next.delete(key);
            return next;
        });
    }, []);

    // SSE mode — with isMounted guard and reconnect timeout cleanup
    useEffect(() => {
        if (!USE_SSE || !chain || !address) return;

        let isMounted = true;
        let reconnects = 0;
        let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
        receivedSections.current = {};
        setSections({});
        setLoadingKeys(new Set(DOSSIER_KEYS));
        setIsComplete(false);
        setReconnectCount(0);

        const connect = () => {
            if (!isMounted) return;

            let token: string | null = null;
            try { token = localStorage.getItem('token'); } catch { /* private mode */ }
            const url = `${API_BASE_URL}/dossier/${chain}/${address}/stream${token ? `?token=${token}` : ''}`;
            const es = new EventSource(url);
            esRef.current = es;

            DOSSIER_KEYS.forEach(key => {
                es.addEventListener(key, (e: MessageEvent) => {
                    if (!isMounted) return;
                    try {
                        const data = JSON.parse(e.data);
                        markSection(key, data);
                    } catch (err) {
                        console.error(`Failed to parse dossier section: ${key}`, err);
                    }
                });
            });

            es.addEventListener('done', () => {
                es.close();  // Explicit close on completion — SSE connection limit fix
                esRef.current = null;
                if (isMounted) setIsComplete(true);
            });

            es.onerror = () => {
                es.close();
                esRef.current = null;
                if (isMounted && reconnects < MAX_RECONNECTS) {
                    reconnects++;
                    setReconnectCount(reconnects);
                    // Exponential backoff: 1s, 2s, 3s
                    reconnectTimeoutId = setTimeout(connect, 1000 * reconnects);
                } else if (isMounted) {
                    // Retries exhausted — mark remaining sections as unavailable
                    // so the spinner stops and user sees "Some data unavailable"
                    setLoadingKeys(prev => {
                        if (prev.size === 0) return prev;
                        prev.forEach(key => {
                            if (!receivedSections.current[key]) {
                                receivedSections.current[key] = {
                                    status: 'unavailable',
                                    reason: 'Connection failed after retries',
                                    fetched_at: new Date().toISOString(),
                                };
                            }
                        });
                        setSections({ ...receivedSections.current });
                        return new Set();
                    });
                }
                // Already-received sections remain visible — no spinner reset
            };
        };

        connect();
        return () => {
            isMounted = false;
            esRef.current?.close();
            esRef.current = null;
            if (reconnectTimeoutId) clearTimeout(reconnectTimeoutId);
        };
    }, [chain, address, markSection]);

    // Mock adapter mode — with isMounted guard
    useEffect(() => {
        if (USE_SSE || !chain || !address) {
            if (!USE_SSE && (!chain || !address)) {
                receivedSections.current = {};
                setSections({});
                setLoadingKeys(new Set());
                setIsComplete(false);
            }
            return;
        }

        let isMounted = true;
        const timeoutIds: ReturnType<typeof setTimeout>[] = [];
        receivedSections.current = {};
        setSections({});
        setLoadingKeys(new Set(DOSSIER_KEYS));
        setIsComplete(false);

        // Identity — immediate from local data
        markSection('identity', {
            address,
            chain,
            fetched_at: new Date().toISOString(),
            status: 'loaded',
        });

        // Fan out remaining sections via existing API (with per-section timeout)
        const fetchSection = async (key: DossierKey, fetcher: () => Promise<any>) => {
            try {
                const result = await Promise.race([
                    fetcher(),
                    new Promise((_, reject) => {
                        const id = setTimeout(
                            () => reject(new Error(`${key} timed out after ${SECTION_TIMEOUT_MS / 1000}s`)),
                            SECTION_TIMEOUT_MS,
                        );
                        timeoutIds.push(id);
                    }),
                ]);
                if (isMounted) {
                    markSection(key, {
                        ...(result as any),
                        fetched_at: new Date().toISOString(),
                        status: 'loaded',
                    });
                }
            } catch (err: any) {
                if (isMounted) {
                    markSection(key, {
                        status: 'unavailable',
                        reason: err?.message || 'Unknown error',
                        fetched_at: new Date().toISOString(),
                    });
                }
            }
        };

        // Wire to real addressService endpoints
        // Note: addressService methods already return response.data, no extra unwrap needed
        Promise.allSettled([
            fetchSection('ofac', () => addressService.checkSanctions(chain, address)),
            fetchSection('profile', () => addressService.getProfile(chain, address)),
            fetchSection('bridges', () => addressService.getBridges(chain, address)),
        ]).then(() => {
            if (isMounted) setIsComplete(true);
        });

        return () => {
            isMounted = false;
            timeoutIds.forEach(clearTimeout);
        };
    }, [chain, address, markSection]);

    const hasError = Object.values(sections).some(s => s?.status === 'unavailable');

    // Memoized section fetchers — prevents stale chain/address closures in refresh()
    const sectionFetchers = useMemo<Record<string, () => Promise<any>>>(() => ({
        ofac: () => addressService.checkSanctions(chain!, address!),
        profile: () => addressService.getProfile(chain!, address!),
        bridges: () => addressService.getBridges(chain!, address!),
    }), [chain, address]);

    const refresh = useCallback((key?: DossierKey) => {
        if (!chain || !address) return;

        if (USE_SSE) {
            // SSE mode: close current connection and reconnect
            esRef.current?.close();
            esRef.current = null;
            // Reset state to trigger the SSE useEffect via dependency change
            setIsComplete(false);
            setReconnectCount(0);
            receivedSections.current = {};
            setSections({});
            setLoadingKeys(new Set(DOSSIER_KEYS));
            return;
        }

        // Mock adapter: re-fetch specific key or all failed sections
        const keysToRefresh = key
            ? [key]
            : (Object.entries(sections)
                .filter(([, v]) => v?.status === 'unavailable')
                .map(([k]) => k as DossierKey));

        if (keysToRefresh.length === 0) return;

        // Use Promise.all instead of forEach(async) for proper error propagation
        Promise.all(keysToRefresh.map(async (k) => {
            const fetcher = sectionFetchers[k];
            if (!fetcher) return;

            setLoadingKeys(prev => new Set(prev).add(k));
            try {
                const result = await Promise.race([
                    fetcher(),
                    new Promise((_, reject) =>
                        setTimeout(
                            () => reject(new Error(`${k} timed out after ${SECTION_TIMEOUT_MS / 1000}s`)),
                            SECTION_TIMEOUT_MS,
                        )
                    ),
                ]);
                markSection(k, {
                    ...(result as any),
                    fetched_at: new Date().toISOString(),
                    status: 'loaded',
                });
            } catch (err: any) {
                markSection(k, {
                    status: 'unavailable',
                    reason: err?.message || 'Unknown error',
                    fetched_at: new Date().toISOString(),
                });
            }
        })).catch(err => {
            console.error('Dossier refresh failed:', err);
        });
    }, [chain, address, sections, markSection, sectionFetchers]);

    return {
        sections,
        loadingKeys,
        reconnectCount,
        isComplete,
        hasError,
        refresh,
    };
}
